import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Tv, Loader2, AlertTriangle } from "lucide-react";
import { t, type Lang } from "@/utils/i18n";
import { useLanguage } from "@/components/providers/language-provider";
import { billingApi } from "@/services/api";
import { paymentCopy } from "@/components/activation/payment-copy";

const content: Record<Lang, {
  title: string;
  heading: string;
  subtitle: string;
  step1: string;
  step2: string;
  step3: string;
  statusText: string;
  cardTitle: string;
  cardSub: string;
  status: string;
  statusValue: string;
  downloadCta: string;
  downloadSub: string;
}> = {
  en: {
    title: "Activation Confirmed - MoovaTV",
    heading: "Payment Confirmed",
    subtitle: "Your TV will activate automatically within seconds. No additional action is required.",
    step1: "Payment received successfully",
    step2: "Device activation sent to your TV",
    step3: "Your TV screen will update automatically",
    statusText: "Activation complete — you can close this page",
    cardTitle: "Device Activated",
    cardSub: "Your media player is ready. Add your own playlist.",
    status: "Status",
    statusValue: "Active",
    downloadCta: "Now download the app to control your TV",
    downloadSub: "Use your phone as a remote control",
  },
  pt: {
    title: "Ativação Confirmada - MoovaTV",
    heading: "Pagamento Confirmado",
    subtitle: "Sua TV será ativada automaticamente em segundos. Nenhuma ação adicional é necessária.",
    step1: "Pagamento recebido com sucesso",
    step2: "Ativação enviada para sua TV",
    step3: "A tela da sua TV será atualizada automaticamente",
    statusText: "Ativação concluída — você pode fechar esta página",
    cardTitle: "Dispositivo Ativado",
    cardSub: "Seu player está pronto. Adicione sua própria playlist.",
    status: "Status",
    statusValue: "Ativo",
    downloadCta: "Agora baixe o app para controlar sua TV",
    downloadSub: "Use seu celular como controle remoto",
  },
  de: {
    title: "Aktivierung Bestätigt - MoovaTV",
    heading: "Zahlung Bestätigt",
    subtitle: "Ihr Fernseher wird innerhalb von Sekunden automatisch aktiviert. Keine weitere Aktion erforderlich.",
    step1: "Zahlung erfolgreich erhalten",
    step2: "Geräteaktivierung an Ihren Fernseher gesendet",
    step3: "Ihr Fernsehbildschirm wird automatisch aktualisiert",
    statusText: "Aktivierung abgeschlossen — Sie können diese Seite schließen",
    cardTitle: "Gerät Aktiviert",
    cardSub: "Ihr Mediaplayer ist bereit. Fügen Sie Ihre eigene Playlist hinzu.",
    status: "Status",
    statusValue: "Aktiv",
    downloadCta: "Laden Sie jetzt die App herunter, um Ihren Fernseher zu steuern",
    downloadSub: "Verwenden Sie Ihr Handy als Fernbedienung",
  },
  fr: {
    title: "Activation Confirmée - MoovaTV",
    heading: "Paiement Confirmé",
    subtitle: "Votre TV sera activée automatiquement en quelques secondes. Aucune action supplémentaire n'est requise.",
    step1: "Paiement reçu avec succès",
    step2: "Activation envoyée à votre TV",
    step3: "L'écran de votre TV se mettra à jour automatiquement",
    statusText: "Activation terminée — vous pouvez fermer cette page",
    cardTitle: "Appareil Activé",
    cardSub: "Votre lecteur est prêt. Ajoutez votre propre playlist.",
    status: "Statut",
    statusValue: "Actif",
    downloadCta: "Téléchargez maintenant l'app pour contrôler votre TV",
    downloadSub: "Utilisez votre téléphone comme télécommande",
  },
  es: {
    title: "Activación Confirmada - MoovaTV",
    heading: "Pago Confirmado",
    subtitle: "Tu TV se activará automáticamente en segundos. No se requiere ninguna acción adicional.",
    step1: "Pago recibido con éxito",
    step2: "Activación enviada a tu TV",
    step3: "La pantalla de tu TV se actualizará automáticamente",
    statusText: "Activación completa — puedes cerrar esta página",
    cardTitle: "Dispositivo Activado",
    cardSub: "Tu reproductor está listo. Añade tu propia lista.",
    status: "Estado",
    statusValue: "Activo",
    downloadCta: "Ahora descarga la app para controlar tu TV",
    downloadSub: "Usa tu celular como control remoto",
  },
  uk: {
    title: "Активацію підтверджено - MoovaTV",
    heading: "Оплату підтверджено",
    subtitle: "Ваш телевізор активується автоматично за кілька секунд. Жодних додаткових дій не потрібно.",
    step1: "Оплату успішно отримано",
    step2: "Активацію пристрою надіслано на ваш телевізор",
    step3: "Екран вашого телевізора оновиться автоматично",
    statusText: "Активацію завершено — можете закрити цю сторінку",
    cardTitle: "Пристрій активовано",
    cardSub: "Ваш програвач готовий. Додайте власний плейлист.",
    status: "Статус",
    statusValue: "Активний",
    downloadCta: "Тепер завантажте застосунок, щоб керувати телевізором",
    downloadSub: "Використовуйте телефон як пульт",
  },
};

const ActivationSuccessPage = () => {
  const { language } = useLanguage();
  const c = content[language] || content.en;
  const messages = paymentCopy[language];
  const [params] = useSearchParams();
  const [txnId] = useState(() => params.get('txn') || sessionStorage.getItem('moova_txn') || '');
  const [status, setStatus] = useState('PENDING');
  const [networkError, setNetworkError] = useState(false);
  const [retry, setRetry] = useState(0);
  const validReference = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(txnId);
  const failed = ['FAILED', 'CANCELLED', 'REFUNDED'].includes(status);

  useEffect(() => {
    if (!validReference) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const result = await billingApi.getCheckoutStatus(txnId);
        if (!active) return;
        setStatus(result.status); setNetworkError(false);
        if (result.status === 'COMPLETED') {
          if (sessionStorage.getItem('moova_txn') === txnId) {
            sessionStorage.removeItem('moova_txn');
            sessionStorage.removeItem('moova_checkout');
          }
          return;
        }
        if (['FAILED', 'CANCELLED', 'REFUNDED'].includes(result.status)) return;
      } catch { if (active) setNetworkError(true); }
      if (active) timer = setTimeout(poll, 3500);
    };
    void poll();
    return () => { active = false; clearTimeout(timer); };
  }, [txnId, validReference, retry]);

  if (status !== 'COMPLETED') return <>
    <Helmet><title>{failed ? messages.failed : messages.checking} — MoovaTV</title></Helmet>
    <div className="mx-auto max-w-xl px-6 py-20 text-center" aria-live="polite">
      <div className="mx-auto mb-6 grid h-20 w-20 place-items-center rounded-full border border-brand-accent/25 bg-brand-accent/10">
        {failed || !validReference ? <AlertTriangle className="h-8 w-8 text-amber-300" /> : <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />}
      </div>
      <h1 className="mb-4 text-3xl font-semibold">{failed ? messages.failed : messages.checking}</h1>
      <p className="text-white/60">{!validReference ? messages.missing : failed ? messages.failedDetail : messages.checkingDetail}</p>
      {networkError && <p role="alert" className="mt-4 text-sm text-amber-300">{messages.network}</p>}
      {validReference && <button className="mt-6 rounded-xl border border-white/15 px-5 py-3 text-sm" onClick={() => { setStatus('PENDING'); setRetry((n) => n + 1); }}>{messages.retry}</button>}
      <a className="mt-6 block text-sm text-brand-sky underline" href={`/activation?mac=${encodeURIComponent(params.get('mac') || '')}`}>{messages.back}</a>
    </div>
  </>;

  return (
    <>
      <Helmet>
        <title>{c.title}</title>
      </Helmet>

      <div className="w-full max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

          {/* ── Left: Confirmation info ── */}
          <div className="space-y-10">
            <div>
              <h1 className="text-4xl lg:text-[56px] font-semibold tracking-tight leading-[1.1] mb-4">
                {c.heading}
              </h1>
              <p className="text-lg text-white/45 font-normal leading-relaxed">
                {c.subtitle}
              </p>
            </div>

            {/* Steps */}
            <div className="flex flex-col gap-7">
              <div className="flex items-center gap-5">
                <div className="w-[52px] h-[52px] rounded-[12px] bg-emerald-500/12 border border-emerald-500/25 flex items-center justify-center text-lg font-semibold text-emerald-400 flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <p className="text-white/75 text-lg">{c.step1}</p>
              </div>
              <div className="flex items-center gap-5">
                <div className="w-[52px] h-[52px] rounded-[12px] bg-emerald-500/12 border border-emerald-500/25 flex items-center justify-center text-lg font-semibold text-emerald-400 flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <p className="text-white/75 text-lg">{c.step2}</p>
              </div>
              <div className="flex items-center gap-5">
                <div className="w-[52px] h-[52px] rounded-[12px] bg-brand-accent/12 border border-brand-accent/25 flex items-center justify-center text-lg font-semibold text-brand-accent flex-shrink-0">
                  <Tv className="h-5 w-5" />
                </div>
                <p className="text-white/75 text-lg">{c.step3}</p>
              </div>
            </div>

            {/* Status bar */}
            <div className="flex items-center gap-4 px-6 py-4 rounded-[14px] bg-emerald-500/[0.06] border border-emerald-500/[0.15]">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-emerald-400 shadow-[0_0_12px_rgba(57,206,62,0.5)]" />
              <span className="text-sm text-emerald-400/80">
                {c.statusText}
              </span>
            </div>
          </div>

          {/* ── Right: Success card ── */}
          <div className="flex justify-center">
            <div className="rounded-[24px] border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl p-12 lg:p-16 flex flex-col items-center text-center">
              <div className="w-[120px] h-[120px] rounded-full bg-emerald-500/12 border-2 border-emerald-500/35 flex items-center justify-center mb-8">
                <CheckCircle2 className="h-14 w-14 text-emerald-400" />
              </div>

              <h2 className="text-2xl font-semibold mb-2">{c.cardTitle}</h2>
              <p className="text-sm text-white/40 mb-8">{c.cardSub}</p>
              <p className="text-xs text-white/45 mb-6">{t('activation', 'disclaimer', language)}</p>

              <div className="w-full space-y-4">
                <div className="flex items-center justify-between px-5 py-3.5 rounded-[12px] bg-white/[0.04] border border-white/[0.06]">
                  <span className="text-xs text-white/40">{c.status}</span>
                  <span className="text-sm text-emerald-400 font-medium">{c.statusValue}</span>
                </div>

                {/* Download CTA */}
                <div className="h-[1px] bg-white/[0.06] my-2" />
                <p className="text-sm text-white/50 font-medium">{c.downloadCta}</p>
                <p className="text-xs text-white/25 mb-2">{c.downloadSub}</p>
                <a
                  href="https://play.google.com/store/apps/details?id=tv.moova.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block hover:opacity-80 transition-opacity"
                >
                  <img
                    src="/assets/google-play-badge.png"
                    alt="Get it on Google Play"
                    className="h-14"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActivationSuccessPage;
